import { createSlice, createSelector } from '@reduxjs/toolkit'
import { createUID } from 'common/utils/createUID'
import { each, inRange, isEmpty, sortBy } from 'lodash'
import { EMPTY_DAY } from './programModels'
import { calculateWorkoutEstimate, getInflationEnabled } from 'common/utils/wktDurationUtils'
import { parseDurationString } from 'common/utils/timeDuration'

export const initialState = {
  workouts: {},
  partsMultiSelected: [],
  partSelectFrom: null,
  partSelectTo: null,
  partsSelected: [],
  workoutsMultiSelected: [],
  workoutSelectFrom: null,
  workoutSelectTo: null,
  workoutsSelected: [],
  partsCopied: [],
  workoutsCopied: [],
  multiWktsCopied: {}, // store when multiWkt is copied so that when it is deleted, it can still be pasted
  multiWktDaysCopied: {}, // store when multiWkt is copied so that when it is deleted, it can still be pasted
  weeksCopied: [],
  maxDayIdx: null,
  wktIdsSorted: {},
  multiWktDays: {},
}

//to create same references and limit re-renders
const selectorVals = {
  dayIdxIsEmpty: {},
}

const programSlice = createSlice({
  name: 'program',
  initialState,
  reducers: {
    draftProgramListened: (state, action) => {
      const { wktIdsSorted, multiWktDays } = action.payload
      if (wktIdsSorted) {
        const maxDayIdx = wktIdsSorted.length - 1

        state.maxDayIdx = maxDayIdx

        state.wktIdsSorted = wktIdsSorted

        selectorVals.dayIdxIsEmpty = {}
      }

      state.multiWktDays = multiWktDays || {}
    },
    draftWorkoutRetrieved: (state, action) => {
      const { workout } = action.payload
      state.workouts[workout.id] = workout
    },
    draftWorkoutsDeleted: (state, action) => {
      const { workoutIds } = action.payload

      if (state.partsSelected.length > 0 || state.workoutsSelected.length > 0) {
        const remainingParts = state.partsSelected.filter(
          (selectedPart) => !workoutIds.includes(selectedPart.workoutId)
        )
        const remainingWkts = state.workoutsSelected.filter((selectedWkt) => !workoutIds.includes(selectedWkt.id))

        state.partsSelected = remainingParts
        state.workoutsSelected = remainingWkts
      }
    },
    partSelected: (state, action) => {
      const { part, workoutId } = action.payload

      const partToSave = { ...part, workoutId }
      const alreadyIncluded = state.partsSelected.find((selectedPart) => selectedPart.id === part.id)

      state.partSelectFrom = partToSave
      state.partSelectTo = initialState.partSelectTo
      state.partsMultiSelected = initialState.partsMultiSelected

      if (!alreadyIncluded) {
        state.partsSelected.push(partToSave)
      } else {
        const selectedRemoved = state.partsSelected.filter((selectedPart) => selectedPart.id !== part.id)
        state.partsSelected = selectedRemoved
      }
    },
    partsMultiSelected: (state, action) => {
      const { workoutId, partIdx: selectToIdx, part: selectToPart } = action.payload

      if (
        state.partSelectTo !== null &&
        selectToPart.id !== state.partSelectTo.id &&
        workoutId === state.partSelectTo.workoutId
      ) {
        const updatedPartsSelected = state.partsSelected.filter(
          (selectedPart) =>
            !state.partsMultiSelected.some((multiSelectedPart) => multiSelectedPart.id === selectedPart.id)
        )
        state.partsSelected = updatedPartsSelected
      }

      if (state.partSelectFrom !== null && state.partSelectFrom.workoutId === workoutId) {
        const wktSelectedParts = state.partsSelected.filter((selectedPart) => selectedPart.workoutId === workoutId)
        const wktParts = state.workouts[workoutId].parts

        const selectFromPartIdx = wktParts.findIndex((part) => part.id === state.partSelectFrom.id)
        const isUpwardSelection = selectToIdx > selectFromPartIdx

        let partsMultiSelected = []
        wktParts.forEach((wktPart, idx) => {
          const partAlreadyIncluded = wktSelectedParts.find((selectedPart) => selectedPart.id === wktPart.id)
          const isPartInRange = isUpwardSelection
            ? inRange(idx, selectFromPartIdx, selectToIdx + 1)
            : inRange(idx, selectToIdx, selectFromPartIdx + 1)

          if (isPartInRange && !partAlreadyIncluded && wktPart.id !== state.partSelectFrom.id) {
            const partToSave = { ...wktPart, workoutId }
            partsMultiSelected.push(partToSave)
          } else if (isPartInRange && partAlreadyIncluded && wktPart.id !== state.partSelectFrom.id) {
            state.partsSelected = state.partsSelected.filter((selectedPart) => selectedPart.id !== wktPart.id)
          }
        })

        const partsSelected = [...state.partsSelected, ...partsMultiSelected]

        state.partsSelected = partsSelected
        state.partSelectTo = { ...selectToPart, workoutId }
        state.partsMultiSelected = partsMultiSelected
      }
    },
    selectedWorkoutsDeleted: (state, action) => {
      const { workoutIds } = action.payload

      const partsRemaining = state.partsSelected.filter((selectedPart) => !workoutIds.includes(selectedPart.workoutId))

      state.partsSelected = partsRemaining
      state.workoutsSelected = initialState.workoutsSelected
    },
    selectedPartsCleared: (state, _) => {
      state.partsSelected = initialState.partsSelected
    },
    workoutSelected: (state, action) => {
      const { workout } = action.payload

      const alreadyIncluded = state.workoutsSelected.find((selectedWkt) => selectedWkt.id === workout.id)

      state.workoutSelectFrom = workout
      state.workoutSelectTo = initialState.workoutSelectTo
      state.workoutsMultiSelected = initialState.workoutsMultiSelected

      if (!alreadyIncluded) {
        state.workoutsSelected.push(workout)
      } else {
        const selectedRemoved = state.workoutsSelected.filter((selectedWkt) => selectedWkt.id !== workout.id)
        state.workoutsSelected = selectedRemoved
      }
    },
    workoutsMultiSelected: (state, action) => {
      const { workout: selectToWkt } = action.payload
      const currProgramWktIds = state.wktIdsSorted
      const allCurrProgramWktsEntries = Object.entries(state.workouts).filter(([wktId, _]) =>
        currProgramWktIds.includes(wktId)
      )
      const allCurrProgramWkts = Object.fromEntries(allCurrProgramWktsEntries)

      // Workout multi select TO anchor was set and is different than current selectToWkt
      if (state.workoutSelectTo !== null && selectToWkt.id !== state.workoutSelectTo.id) {
        // Remove previously multi selected workouts
        const updatedWktsSelected = state.workoutsSelected.filter(
          (selectedWkt) =>
            !state.workoutsMultiSelected.some((multiSelectedWkt) => multiSelectedWkt.id === selectedWkt.id)
        )
        state.workoutsSelected = updatedWktsSelected
      }

      if (state.workoutSelectFrom !== null) {
        const selectFromWkt = state.workoutSelectFrom
        const isUpwardSelection = selectToWkt.dayIdx > selectFromWkt.dayIdx

        let wktsMultiSelected = []
        each(allCurrProgramWkts, (wkt) => {
          const isWktSelectable = wkt.type !== EMPTY_DAY.type
          const wktAlreadyIncluded = state.workoutsSelected.find((selectedWkt) => selectedWkt.id === wkt.id)
          const isWktInRange = isUpwardSelection
            ? inRange(wkt.dayIdx, selectFromWkt.dayIdx, selectToWkt.dayIdx + 1)
            : inRange(wkt.dayIdx, selectToWkt.dayIdx, selectFromWkt.dayIdx + 1)

          if (isWktInRange && isWktSelectable && !wktAlreadyIncluded && wkt.id !== selectFromWkt.id) {
            wktsMultiSelected.push(wkt)
          } else if (isWktInRange && isWktSelectable && wktAlreadyIncluded && wkt.id !== selectFromWkt.id) {
            state.workoutsSelected = state.workoutsSelected.filter((selectedWkt) => selectedWkt.id !== wkt.id)
          }
        })

        const wktsSelected = [...state.workoutsSelected, ...wktsMultiSelected]
        const sortedWktsSelected = sortBy(wktsSelected, 'dayIdx')

        state.workoutsSelected = sortedWktsSelected
        state.workoutSelectTo = selectToWkt
        state.workoutsMultiSelected = wktsMultiSelected
      }
    },
    selectedWorkoutsCleared: (state, _) => {
      state.workoutsSelected = initialState.workoutsSelected
    },
    partsCopied: (state, action) => {
      const { parts } = action.payload
      const updatedParts = parts.map((part) => ({ ...part, id: createUID() }))

      state.partsCopied = updatedParts
    },
    copiedPartsCleared: (state, _) => {
      state.partsCopied = initialState.partsCopied
    },
    workoutsCopied: (state, action) => {
      const { workouts, multiWktsCopied, multiWktDaysCopied } = action.payload

      state.workoutsCopied = workouts
      state.multiWktsCopied = multiWktsCopied
      state.multiWktDaysCopied = multiWktDaysCopied
    },
    copiedWorkoutsCleared: (state, _) => {
      state.workoutsCopied = initialState.workoutsCopied
      state.multiWktsCopied = initialState.multiWktsCopied
      state.multiWktDaysCopied = initialState.multiWktDaysCopied
    },
    multiSelectReferencesCleared: (state, _) => {
      state.partsSelected = initialState.partsSelected
      state.workoutsSelected = initialState.workoutsSelected
      state.partsMultiSelected = initialState.partsMultiSelected
      state.partSelectFrom = initialState.partSelectFrom
      state.partSelectTo = initialState.partSelectTo
      state.workoutsMultiSelected = initialState.workoutsMultiSelected
      state.workoutSelectFrom = initialState.workoutSelectFrom
      state.workoutSelectTo = initialState.workoutSelectTo
    },
    weeksCopied: (state, action) => {
      const { week } = action.payload
      state.weeksCopied = [week]
    },
    copiedWeeksCleared: (state, _) => {
      state.weeksCopied = initialState.weeksCopied
    },
  },
})

export const {
  draftProgramListened,
  draftWorkoutRetrieved,
  draftWorkoutsDeleted,
  partSelected,
  partsMultiSelected,
  selectedWorkoutsDeleted,
  selectedPartsCleared,
  workoutsMultiSelected,
  workoutSelected,
  selectedWorkoutsCleared,
  partsCopied,
  copiedPartsCleared,
  workoutsCopied,
  copiedWorkoutsCleared,
  multiSelectReferencesCleared,
  weeksCopied,
  copiedWeeksCleared,
} = programSlice.actions

export const getMultiWktDurationEst = createSelector(
  (state) => state.program.workouts,
  (_, workoutIds) => workoutIds,
  (_, __, extraData) => extraData,
  (workouts, workoutIds, extraData) => {
    if (!workoutIds?.length) {
      return {
        totalMultiWktSeconds: 0,
        lowConfMultiWktIdxs: [],
        isMultiWktsLoading: false,
      }
    } else {
      const multiWkts = workoutIds.map((id) => workouts[id])

      let isMultiWktsLoading = false
      let totalMultiWktSeconds = 0
      let lowConfMultiWktIdxs = []
      for (let wktIdx = 0; wktIdx < multiWkts.length; wktIdx++) {
        const wkt = multiWkts[wktIdx]
        if (isEmpty(wkt)) {
          isMultiWktsLoading = true
          break
        } else if (wkt?.type === 'workout') {
          const inflationEnabled = getInflationEnabled({
            wktDurationInflationEnabled: wkt?.enableWktDurationInflation,
            orgWktDurationInflationEnabled: extraData?.orgWktDurationInflationEnabled,
          })
          const { totalSeconds, hasLowConfidenceEx } = calculateWorkoutEstimate({
            workout: wkt,
            orgExercises: extraData?.orgExercises,
            orgBenchmarks: extraData?.orgBenchmarks,
            inflationEnabled,
          })
          totalMultiWktSeconds += totalSeconds
          if (hasLowConfidenceEx) {
            lowConfMultiWktIdxs.push(wktIdx)
          }
        } else if (wkt?.type === 'single_video') {
          const videoId = wkt?.videoId
          const videoDuration = extraData?.videos?.[videoId]?.duration || '00:00:00.00'
          const videoSeconds = parseDurationString(videoDuration)
          totalMultiWktSeconds += videoSeconds
        }
      }

      return {
        totalMultiWktSeconds,
        lowConfMultiWktIdxs,
        isMultiWktsLoading,
      }
    }
  }
)

export const getPartsSelected = createSelector(
  (state) => state.program.partsSelected,
  (partsSelected) => partsSelected,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getIsPartSelected = createSelector(
  (state) => state.program.partsSelected,
  (_, partId) => partId,
  (partsSelected, partId) => (partsSelected.find((selectedPart) => selectedPart.id === partId) ? true : false)
)

export const getWorkoutsSelected = createSelector(
  (state) => state.program.workoutsSelected,
  (workoutsSelected) => workoutsSelected,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getIsWorkoutSelected = createSelector(
  (state) => state.program.workoutsSelected,
  (_, workoutId) => workoutId,
  (workoutsSelected, workoutId) => (workoutsSelected.find((selectedWkt) => selectedWkt.id === workoutId) ? true : false)
)

export const getDayIdxIsEmpty = createSelector(
  (state) => state.program.workouts,
  (state) => state.program.wktIdsSorted,
  (workouts, wktIdsSorted) => {
    let dayIdxIsEmpty = selectorVals.dayIdxIsEmpty

    const programWkts = {}
    each(wktIdsSorted, (wktId) => {
      programWkts[wktId] = workouts[wktId]
    })

    each(programWkts, (wkt) => {
      if (!wkt) return
      const { type, dayIdx, multiWktDayId } = wkt
      const isEmptyDay = type === 'empty' && !multiWktDayId ? true : false
      dayIdxIsEmpty[dayIdx] = isEmptyDay
    })

    return dayIdxIsEmpty
  }
)

export const getWktsByIdsDragged = createSelector(
  (state) => state.program.workouts,
  (_, workoutIds) => workoutIds,
  (state) => state.program.multiWktDays,
  (workouts, workoutIds, multiWktDays) => {
    const workoutValues = Object.values(workouts)
    const wkts = workoutValues.filter((workout) => workoutIds.includes(workout.id))
    const workoutsDragged = wkts.map((wkt) => {
      if (wkt.multiWktDayId) {
        const { title, instructions } = multiWktDays[wkt.multiWktDayId] || {}
        return { ...wkt, title, instructions }
      } else {
        return wkt
      }
    })
    return workoutsDragged
  }
)

export const getProgramMultiWktDays = createSelector(
  (state) => state.program.multiWktDays,
  (multiWktDays) => multiWktDays,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getProgramMultiWktDaysCopied = createSelector(
  (state) => state.program.multiWktDaysCopied,
  (multiWktDaysCopied) => multiWktDaysCopied,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getPartsCopied = createSelector(
  (state) => state.program.partsCopied,
  (partsCopied) => partsCopied,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getWorkoutsCopied = createSelector(
  (state) => state.program.workoutsCopied,
  (workoutsCopied) => workoutsCopied,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export const getWeeksCopied = createSelector(
  (state) => state.program.weeksCopied,
  (weeksCopied) => weeksCopied,
  {
    devModeChecks: { identityFunctionCheck: 'never' },
  }
)

export default programSlice.reducer
